/**
 * FIXTURE DETAILS FORM
 * 
 * Renders the detail fields for a fixture based on its definition
 * Supports: select, number, text, textarea, checkbox
 */

import React from 'react';

const FixtureDetails = ({ fixture, details = {}, onChange, disabled = false }) => {
  const handleChange = (key, value) => {
    onChange(key, value);
  };

  const renderField = (key, fieldDef) => {
    const value = details[key];
    
    switch (fieldDef.type) {
      case 'select':
        return (
          <div key={key} className="flex-1 min-w-[140px]">
            <label className="block text-xs text-slate-500 mb-1">{fieldDef.label}</label>
            <select
              value={value || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={disabled}
              className="w-full px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            >
              <option value="">Select...</option>
              {fieldDef.options.map(opt => (
                <option key={opt} value={opt}>
                  {typeof opt === 'string' 
                    ? opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    : opt}
                </option>
              ))}
            </select>
          </div>
        );
        
      case 'number':
        return (
          <div key={key} className="flex-1 min-w-[100px]">
            <label className="block text-xs text-slate-500 mb-1">{fieldDef.label}</label>
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleChange(key, e.target.value ? Number(e.target.value) : null)}
              min={fieldDef.min}
              max={fieldDef.max}
              disabled={disabled}
              className="w-full px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
          </div>
        );
        
      case 'text':
        return (
          <div key={key} className="flex-1 min-w-[140px]">
            <label className="block text-xs text-slate-500 mb-1">{fieldDef.label}</label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={disabled}
              className="w-full px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
          </div>
        );
        
      case 'textarea':
        return (
          <div key={key} className="w-full">
            <label className="block text-xs text-slate-500 mb-1">{fieldDef.label}</label>
            <textarea
              value={value || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={disabled}
              rows={2}
              className="w-full px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            />
          </div>
        );
        
      case 'checkbox':
        return (
          <div key={key} className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`detail-${key}`}
              checked={value || false}
              onChange={(e) => handleChange(key, e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 bg-slate-950 border-slate-700 rounded text-emerald-500
                focus:ring-emerald-500 focus:ring-offset-slate-900"
            />
            <label htmlFor={`detail-${key}`} className="text-xs text-slate-400">
              {fieldDef.label}
            </label>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (!fixture.details || Object.keys(fixture.details).length === 0) {
    return null;
  }

  // Separate checkboxes from other fields
  const checkboxFields = [];
  const otherFields = [];
  
  Object.entries(fixture.details).forEach(([key, fieldDef]) => {
    if (fieldDef.type === 'checkbox') {
      checkboxFields.push([key, fieldDef]);
    } else {
      otherFields.push([key, fieldDef]);
    }
  });

  return (
    <div className="space-y-3 mt-3">
      {/* Non-checkbox fields in a flex wrap */}
      {otherFields.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {otherFields.map(([key, fieldDef]) => renderField(key, fieldDef))}
        </div>
      )}
      
      {/* Checkbox fields in a separate row */}
      {checkboxFields.length > 0 && (
        <div className="flex flex-wrap gap-4 pt-1">
          {checkboxFields.map(([key, fieldDef]) => renderField(key, fieldDef))}
        </div>
      )}
    </div>
  );
};

export default FixtureDetails;
